import { useEffect, useRef, useState } from "react";
import { auth, takeAuthCallback, type Session } from "./auth/client";
import { AuthPage } from "./auth/AuthPage";
import { LandingPage } from "./landing/LandingPage";
import { ProductClient } from "./api/client.js";
import { AdminConsole } from './admin/AdminConsole';
import { ClinicianWorkspace } from './clinician/ClinicianWorkspace';
import { ApiConsole } from './api-console/ApiConsole';
import { OriginalDemo } from "./demo/OriginalDemo";
import type { Api, Profile } from "./types";
import { PreferencesProvider, usePreferences } from "./preferences/PreferencesProvider";
import { useI18n } from "./i18n/I18nProvider";
import { localizeError } from "./auth/localizeError";
import { Onboarding } from "./onboarding/Onboarding";
import "../styles/tokens.css";
import "../styles/app.css";
import "./product.css";
import "./legacy-ui.css";

const callback = takeAuthCallback(window.location.href, window.history);

export default function ProductApp() {
  const [view, setView] = useState(() => callback.verifyToken || callback.resetToken ? "login" : window.location.hash.slice(1));
  useEffect(() => {
    const navigate = () => setView(window.location.hash.slice(1));
    window.addEventListener("hashchange", navigate);
    return () => window.removeEventListener("hashchange", navigate);
  }, []);
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  const [resetToken, setResetToken] = useState(callback.resetToken);
  const initialization = useRef<Promise<Session | null> | null>(null);
  const hadSession = useRef(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    let alive = true;
    const subscription = auth.onAuthStateChange((_event, next) => {
      if (alive) {
        setSession(next);
        if (_event === "SIGNED_IN") window.location.hash = "workspace";
        if (_event === "SIGNED_OUT" && hadSession.current) window.location.hash = "home";
        hadSession.current = !!next;
      }
    });
    if (!initialization.current) {
      const token = callback.verifyToken;
      callback.verifyToken = null;
      callback.resetToken = null;
      initialization.current = (token ? auth.verifyEmail(token).then(() => {
        setVerified(true);
      }) : Promise.resolve()).then(() => auth.getSession());
    }
    initialization.current.then(next => {
      if (alive) setSession(next);
    }).catch(error => {
      if (alive) setError(error);
    }).finally(() => {
      if (alive) setReady(true);
    });
    return () => {
      alive = false;
      subscription.unsubscribe();
    };
  }, []);
  // Render the public page immediately; a slow auth refresh must not hide it.
  const showLanding = !resetToken && (["home", "models", "api"].includes(view) || (!session && !["login", "signup", "workspace", "admin", "api-console", "clinician"].includes(view.split("/")[0])));
  return <PreferencesProvider key={session?.user.id || "anonymous"}>
    {showLanding ? <LandingPage authenticated={!!session} section={view === "models" ? "models" : view === "api" ? "api" : "home"} /> : <Entry authenticated={!!session && !resetToken} ready={ready} error={error} clearError={() => setError(null)} verified={verified}>
      {!session || resetToken ? <AuthPage key={view} initialMode={view === "signup" ? "signup" : "login"} resetToken={resetToken} onRecovered={() => { setResetToken(null); window.location.hash = "login"; }} />
        : <Account key={session.user.id} userId={session.user.id} view={view} />}
    </Entry>}
  </PreferencesProvider>;
}
function Entry({ authenticated, ready, error, clearError, verified, children }: {
  authenticated: boolean; ready: boolean; error: unknown; clearError: () => void; verified: boolean; children: React.ReactNode;
}) {
  const { t, locale } = useI18n();
  return <div className={authenticated ? "authenticated-demo-root" : "product lyncia-product"}>
    {Boolean(error) && <div role="alert" className="notice error">{localizeError(error, locale)}<button onClick={clearError}>{t("关闭", "Close")}</button></div>}
    {verified && <p role="status" className="notice">{t("邮箱验证成功，请登录你的账户。", "Email verified. Please sign in.")}</p>}
    {ready ? children : <EntryLoading />}
  </div>;
}

function Account({ userId, view }: { userId: string; view: string }) {
  const [profile, setProfile] = useState<Profile | null>(null),
    [error, setError] = useState<unknown>(null);
  const { t, locale } = useI18n();
  const [client] = useState(
    () =>
      new ProductClient(async (refresh = false) => {
        const session = await (refresh ? auth.refreshSession() : auth.getSession());
        return session?.user.id === userId ? session.access_token : null;
      }) as Api,
  );
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    client
      .request<Profile>("/me")
      .then((p) => {
        if (alive.current) setProfile(p);
      })
      .catch((e) => {
        if (alive.current) setError(e);
      });
    return () => {
      alive.current = false;
      client.dispose();
    };
  }, [client]);
  async function logout() {
    client.dispose();
    try {
      await auth.signOut();
    } catch {
      // The local identity is already cleared; the next login replaces the cookie.
    }
  }
  if (!profile && !error) return <EntryLoading />;
  if (!profile || profile.status !== "active") return <main className="product-auth"><div className="auth-card">
    <h2>{!profile ? t("账户信息加载失败", "Could not load your account") : t("账户暂不可用", "Account unavailable")}</h2>
    {Boolean(error) && <p className="notice error" role="alert">{localizeError(error, locale)}</p>}
    <button className="quiet" onClick={() => void logout()}>{t("退出登录", "Sign out")}</button>
  </div></main>;
  return <PreferencesProvider key={userId} api={client} initialProfile={profile}>
    <AccountContent api={client} logout={logout} view={view} />
  </PreferencesProvider>;
}
function AccountContent({ api, logout, view }: { api: Api; logout: () => Promise<void>; view: string }) {
  const { profile, replaceProfile } = usePreferences();
  const { t } = useI18n();
  if (view === 'clinician') return profile?.roles.includes('clinician') ? <ClinicianWorkspace api={api} /> : <main><p>{t('需要咨询师权限', 'Clinician access required')}</p><a href="#workspace">{t('返回我的空间', 'Back to my space')}</a></main>;
  if (view === 'admin') return profile?.roles.includes('admin') ? <AdminConsole api={api} /> : <main><p>{t('需要管理员权限', 'Administrator access required')}</p><a href="#workspace">{t('返回咨询', 'Back to chat')}</a></main>;
  if (view.split('/')[0] === 'api-console') return <ApiConsole api={api} section={view.split('/')[1]} />;
  const status = profile?.onboarding?.status;
  // Old profiles without onboarding metadata keep their existing workspace access.
  if (status === "not_started" || status === "in_progress") return <div className="product lyncia-product"><Onboarding /></div>;
  return <OriginalDemo api={api} logout={logout} />;
}
function EntryLoading() {
  const { t } = useI18n();
  return <div className="legacy-entry-loading" role="status"><span className="sr-only">{t("正在加载…", "Loading…")}</span></div>;
}
